import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { memo } from 'react';
import type { ReactNode } from 'react';
import useDrawerStore from '@/stores/useDrawerStore';
import useChatStore from '@/stores/useChatStore';
import { useSidebar } from '@ui/sidebar';

// #endregion

// --------------------------------------------------------------------------------
// Component Implementation
// --------------------------------------------------------------------------------

// #region REACT.FC
/**
 * Drawer Header component with close button and optional actions
 * 
 * Feature Flow:
 * - Shows drawer title or custom header content
 * - Displays optional action buttons (e.g., create button)
 * - Provides close button that closes the drawer completely
 * - Handles AI chat store synchronization for ai-chat drawer type
 * 
 * @param props - Component props
 * @param props.title - Title to display (used when customHeader is not provided)
 * @param props.drawerType - Type of drawer
 * @param props.actions - Optional action buttons to render (used when customHeader is not provided)
 * @param props.customHeader - Custom header content that fully replaces title and actions
 * @returns Drawer header UI or null if drawer is not active
 */
function DrawerHeader({ title, drawerType, actions, customHeader }: DrawerHeaderProps) {
  // Get drawer state and actions from Zustand store
  const activeDrawer = useDrawerStore((state) => state.activeDrawer);
  const closeDrawer = useDrawerStore((state) => state.closeDrawer);
  const setAIChatOpen = useChatStore((state) => state.setAIChatOpen);
  const { isMobile, setOpenMobile } = useSidebar();

  // Check if this drawer is currently active
  const isActive = activeDrawer === drawerType;

  // Don't render if drawer is not active
  if (!isActive) return null;

  /**
   * Handle close button click
   * Closes the drawer and syncs AI chat store if needed
   */
  const handleClose = () => {
    closeDrawer();
    if (drawerType === 'ai-chat') {
      setAIChatOpen(false);
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <div className="text-foreground flex shrink-0 items-center justify-between border-none bg-surface-light-sl-1 px-4 py-2 pb-1 dark:border-gray-700 dark:bg-gray-900">
      {/* Render custom header or default title/actions */}
      {customHeader ? (
        // Custom header fully replaces title and actions
        <div className="flex-1 flex items-center justify-between">
          {customHeader}
        </div>
      ) : (
        // Default: title with optional actions
        <>
          <h2 className="text-foreground select-none text-lg font-semibold">{title}</h2>
          {actions && (
            <div className="flex items-center gap-2 ml-auto mr-2">
              {actions}
            </div>
          )}
        </>
      )}
      {/* Close button - always on the right */}
      <div className="flex items-center gap-2 ml-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={handleClose}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
            >
              <X className="text-foreground h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Close</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
// #endregion

export default memo(DrawerHeader);

// --------------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------------

// #region TYPES
/**
 * Drawer type identifier
 */
type DrawerType = 'groups' | 'search' | 'notifications' | 'tasks' | 'ai-chat';

/**
 * Props for DrawerHeader component
 */
interface DrawerHeaderProps {
  /**
   * Title to display in the header (used when customHeader is not provided)
   */
  title: string;
  /**
   * Type of drawer this header belongs to
   */
  drawerType: DrawerType;
  /**
   * Optional action buttons to display in the header (e.g., "Create" button)
   * Used when customHeader is not provided
   */
  actions?: ReactNode;
  /**
   * Custom header content that fully replaces title and actions
   * Close button will still be rendered on the right
   */
  customHeader?: ReactNode;
}
// #endregion

